import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, MessageSquare, Phone, Sparkles } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import { Fold, FoldBody } from "@/components/layout/Fold";
import { LandingHead } from "@/components/shop/GroupCard";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { StoreButton } from "@/components/Buttons";
import { Chapter, CursorTrail, GuideProvider, ScrollProgress, useGuide } from "@/vault/legacy_extras/webdemos/Guide";
import { useMall } from "@/context/MallContext";
import { themeOf } from "@/lib/mall";
import * as B from "@/vault/legacy_extras/webdemos/ButtonDemos";
import * as G from "@/vault/legacy_extras/webdemos/GalleryDemos";
import * as F from "@/vault/legacy_extras/webdemos/FormDemos";
import * as A from "@/vault/legacy_extras/webdemos/AppDemos";
import * as P from "@/vault/legacy_extras/webdemos/PassTwoDemos";

const inputCls = "w-full border-2 border-zinc-900 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900/30";

const DAYS = [
    ["Day 1", "Coffee", "We talk, take notes, sketch it on a park bench if that's where you are."],
    ["Day 2", "Design", "Rough layouts, color, type. You react — we adjust."],
    ["Day 3", "Design", "Everything locked. You approve the direction."],
    ["Day 4", "Build", "AI-assisted build. Real code, real pages, no template."],
    ["Day 5", "Build", "Backend wired, your content loaded, payments connected."],
    ["Day 6", "Polish", "Speed, mobile, edge cases. Every button feels right."],
    ["Day 7", "Launch", "Live URL. Training call. You keep the keys."],
];

const GETS = [
    "Custom, interactive site — not a template",
    "A backend you can actually edit",
    "Stripe payments live on day one",
    "Real design, tuned to your business",
    "AI capability that didn't exist last year",
    "Hosting, domain help + training call included",
];

const VALUE = [
    ["$50k–$250k", "What an agency quotes for a custom, interactive site.", "Agencies bill hours. We bill outcomes — and AI does the boring 80%."],
    ["6 months", "How long that same site usually takes to ship.", "Six months is two seasons of sales you'll never get back."],
    ["7 days", "What it takes us — start to live URL.", "Day 1 coffee, day 7 live. That's the whole pitch."],
    ["A few thousand", "What you actually pay us.", "Same technology the big shops use, priced for a Brooklyn business."],
];

/** The quote form — at the very top of the page now (Phase 10). */
function QuoteForm({ theme }) {
    const { tried } = useGuide();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const { slug: activeSlug, homeSlug } = useMall();
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name || !form.email) { toast.error("Name and email required"); return; }
        setBusy(true);
        try {
            await api.post("/leads", { ...form, topic: "website-design", store_slug: activeSlug || homeSlug || null, message: `${form.message}${tried ? ` · played with ${tried} demo features` : ""}` });
            setDone(true);
            toast.success("Got it — we'll text you today.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (done) return (
        <div className="border-2 border-zinc-900 p-8 text-center" data-testid="web-done">
            <Check size={44} className="mx-auto" style={{ color: theme.primary }} />
            <h2 className="mt-4 font-display text-2xl font-black uppercase tracking-tight">You're on the board.</h2>
            <p className="mt-2 text-zinc-700">We'll text you today to book the coffee. Day 1 can be tomorrow.</p>
        </div>
    );

    return (
        <form onSubmit={submit} className="border-2 border-zinc-900 bg-white p-5 sm:p-7" data-testid="web-quote-form" id="quote">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: theme.primary }}>Get a quote · no deposit today</p>
            <h2 className="mt-2 font-display text-xl font-black uppercase tracking-tight text-zinc-900 md:text-2xl">Tell us what you dreamed up.</h2>
            <p className="mt-1 text-sm text-zinc-600">We talk first, quote flat, then start. Prefer typing? <a href="sms:+13475733875?body=I%20want%20a%20new%20website." className="font-semibold text-zinc-900 underline">Text (347) 573-3875</a>.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input className={inputCls} placeholder="Name *" required value={form.name} onChange={set("name")} data-testid="web-name" />
                <input className={inputCls} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="web-phone" />
                <input className={`${inputCls} sm:col-span-2`} type="email" required placeholder="Email *" value={form.email} onChange={set("email")} data-testid="web-email" />
                <textarea className={`${inputCls} sm:col-span-2`} rows={3} placeholder="What's the business, and what should the site do?" value={form.message} onChange={set("message")} data-testid="web-message" />
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
                <StoreButton theme={theme} type="submit" disabled={busy} data-testid="web-submit" className="px-5 py-3 text-xs">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Start my 7 days
                </StoreButton>
                <a href="tel:+13475733875" className="inline-flex items-center gap-2 border-2 border-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-wide text-zinc-900 hover:bg-zinc-900 hover:text-white"><Phone size={14} /> Call</a>
                <a href="sms:+13475733875?body=I%20want%20a%20new%20website." className="inline-flex items-center gap-2 border-2 border-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-wide text-zinc-900 hover:bg-zinc-900 hover:text-white"><MessageSquare size={14} /> Text</a>
            </div>
        </form>
    );
}

function ValueRow({ theme }) {
    const { say } = useGuide();
    return (
        <FoldBody>
            <div className="grid gap-4 md:grid-cols-4" data-testid="value-row">
                {VALUE.map(([big, small, pitch], i) => (
                    <button key={big} onClick={() => say(pitch, `value-${i}`)} className="border-2 border-zinc-900 bg-white p-5 text-left transition-colors hover:bg-zinc-50" data-testid={`value-card-${i}`}>
                        <p className="font-display text-3xl font-black tracking-tight" style={{ color: theme.primary }}>{big}</p>
                        <p className="mt-2 text-sm text-zinc-700">{small}</p>
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">tap for the truth</p>
                    </button>
                ))}
            </div>
            <div className="mt-4 border-2 border-zinc-900 p-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: theme.primary }}>Everything, day one</p>
                <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
                    {GETS.map((g) => <li key={g} className="flex gap-2 text-sm text-zinc-800"><Check size={14} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {g}</li>)}
                </ul>
            </div>
        </FoldBody>
    );
}

function Process({ theme }) {
    const { say } = useGuide();
    return (
        <FoldBody>
            <p className="max-w-2xl text-base text-zinc-600">Seven days. You're involved for about two of them.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="process-days">
                {DAYS.map(([day, title, desc], i) => (
                    <button key={day} onClick={() => say(`${day} — ${desc}`, `day-${i}`)} className="h-full w-full border-2 border-zinc-900 bg-white p-4 text-left transition-colors hover:bg-zinc-50" data-testid={`process-day-${i}`}>
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: theme.primary }}>{day}</p>
                        <p className="mt-1.5 font-display text-lg font-black uppercase">{title}</p>
                        <p className="mt-1 text-xs text-zinc-600">{desc}</p>
                    </button>
                ))}
            </div>
        </FoldBody>
    );
}

function Playground() {
    return (
        <>
            <ScrollProgress />
            <CursorTrail />
            <FoldBody className="pb-6">
                <p className="max-w-2xl text-base text-zinc-700">
                    Every card below is live. Click, drag, swipe, break it — the guide in the corner tells you what you're looking at
                    and what it does for your business. Keep a mental list; we'll build the ones you liked. <strong>Five chapters, 70+ things to touch.</strong>
                </p>
            </FoldBody>
            <Chapter n={1} id="ch-buttons" accent="#2563EB" texture="dots" title="Buttons & micro-interactions"
                blurb="The smallest things on a site. Also the only things people actually touch."
                statement="Fifteen buttons. Pick the one that feels like your business and we'll build it.">
                <B.PlainButton /><B.GlowButton /><B.RippleButton />
                <B.LoadingButton /><B.MagneticButton /><B.IconButtons />
                <B.GradientButton /><B.CopyButton /><B.ConfettiButton />
                <B.LockedButton /><B.DestructiveButton /><B.VideoButton />
                <B.ArrowLinkDemo /><B.TiltCard /><B.SpotlightCard />
                <P.GlowCursorButtonDemo /><P.SoundToggleDemo /><P.ClickHeatDemo />
            </Chapter>
            <Chapter n={2} id="ch-motion" accent="#7dd3fc" texture="grid" title="Text, motion & attention"
                blurb="How a page holds someone for 90 seconds instead of four."
                statement="Movement is the difference between a brochure and a storefront that's open.">
                <B.CounterDemo /><B.MarqueeDemo /><B.TypewriterDemo />
                <G.FullscreenHeroDemo /><G.ParallaxDemo /><G.FullBleedDemo />
                <P.ScrollStoryDemo /><P.StickyCardsDemo />
                <P.CountdownDemo /><P.StatBarsDemo /><P.TickerNewsDemo />
                <P.LightModeDemo />
            </Chapter>
            <Chapter n={3} id="ch-galleries" accent="#fbbf24" texture="diagonal" title="Galleries & storytelling"
                blurb="Your work, shown a dozen different ways. Swipe, zoom, drag, deal, read."
                statement="If your work is good, the gallery is your whole sales team.">
                <G.CarouselDemo /><G.SwipeStripDemo />
                <G.LightboxDemo /><G.BeforeAfterDemo /><G.HoverZoomDemo />
                <G.MasonryDemo /><G.StackedCardsDemo />
                <G.ComicStripDemo /><G.VerticalFeedDemo />
                <P.ComicReaderDemo /><P.FlipCardDemo />
                <P.SplitHoverDemo /><P.HotspotDemo />
            </Chapter>
            <Chapter n={4} id="ch-forms" accent="#f472b6" texture="dots" title="Forms that actually get filled"
                blurb="Every extra field costs you a customer. None of these feel like forms."
                statement="A quote form nobody finishes is just a very expensive dead end.">
                <F.MultiStepForm /><F.LiveValidationForm /><F.SliderQuote />
                <F.StarRating /><F.FileDropDemo /><F.AutocompleteDemo />
                <F.ToggleDemo /><F.AccordionDemo /><F.DatePickerDemo />
                <F.StepperDemo /><P.TabsDemo /><P.TooltipDemo />
                <P.FilterChipsDemo /><P.DragDropDemo />
            </Chapter>
            <Chapter n={5} id="ch-money" accent="#fb923c" texture="grid" title="The parts that make money"
                blurb="Carts, quotes, bookings, dashboards. Real features, actually running right here."
                statement="This is the chapter that pays for the website in the first month.">
                <A.LiveInventoryDemo /><A.AddToCartDemo /><A.ConfiguratorDemo />
                <A.PricingTableDemo /><A.CouponDemo />
                <A.BookingDemo /><A.ChatQuoteDemo />
                <A.MapDemo /><A.DashboardDemo /><A.NotificationDemo />
                <A.OrderTrackerDemo /><A.ReviewsDemo /><A.PortalDemo />
                <P.LoyaltyCardDemo /><P.ModalDemo /><P.ToastStackDemo />
                <P.ScratchRevealDemo />
                <A.MallDemo />
            </Chapter>
        </>
    );
}

/** WEBSITES (Phase 10): the quote form first; the pitch, the 7-day process and the 70-demo playground wait in collapsed windows. */
export default function WebsiteDesign() {
    const { store } = useMall();
    const theme = themeOf(store);
    const openPlayground = typeof window !== "undefined" && window.location.hash === "#playground";
    return (
        <Layout title="Websites">
            <GuideProvider startHidden>
                <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="websites-page">
                    <Crumbs trail={[{ label: "Websites" }]} />
                    <div className="mt-4 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] lg:items-start">
                        <Reveal>
                            <LandingHead kicker="Website design · built with AI · live in 7 days" testid="websites-head"
                                title={<span data-testid="webdesign-title">A custom website for any business.</span>}
                                line="Restaurants, contractors, studios, stores — not just print shops. An agency quotes $50,000 and six months. We build a real site with a real backend in a week, for a few thousand dollars. Try the demo playground below, then tell us what you want." />
                            <ul className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="websites-gets">
                                {GETS.map((g) => <li key={g} className="flex gap-2 text-sm text-zinc-800"><Check size={15} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {g}</li>)}
                            </ul>
                        </Reveal>
                        <QuoteForm theme={theme} />
                    </div>
                </section>
                <Fold title="What an agency charges — and what we charge" kicker="The numbers" testid="websites-fold-value"><ValueRow theme={theme} /></Fold>
                <Fold title="How the 7 days work" kicker="Park bench to live URL" testid="websites-fold-process" id="process"><Process theme={theme} /></Fold>
                <Fold title="Try the demo playground — 70+ live features" kicker="Touch everything · nothing leaves this page" testid="websites-fold-playground" id="playground" defaultOpen={openPlayground}>
                    <Playground />
                </Fold>
            </GuideProvider>
        </Layout>
    );
}
